import { StripeError, loadStripe } from '@stripe/stripe-js';
import { StateCreator } from 'zustand';

import paymentApi, {
  BillingInfo,
  StripeBillingInfoDefaultValues,
} from '../../api/payment';
import RemoteDataStatus from '../../types/RemoteDataStatus';

import {
  PaymentStore,
  OperationResult,
  IntentResult,
  PaymentErrorKind,
  TransactionError,
} from './types';

// Keeping this for backward compatibility
// TODO: Remove this after backend and frontend are released
const stripeKey =
  process.env.NEXT_PUBLIC_STRIPE_KEY ?? process.env.REACT_APP_STRIPE_KEY;

const stripeEUKey =
  process.env.NEXT_PUBLIC_STRIPE_EU_KEY ??
  process.env.REACT_APP_STRIPE_EU_KEY ??
  stripeKey;

const stripeUSKey =
  process.env.NEXT_PUBLIC_STRIPE_US_KEY ?? process.env.REACT_APP_STRIPE_US_KEY;

const initialState = {
  stripeAccountCountry: null,
  stripe: null,
  status: RemoteDataStatus.Initial,
  payment: null,
  promoCode: null,
  errorKind: null,
  billingInfo: null,
  transactionError: null,
};

const fetchErrorCode = async (
  error: StripeError | Error,
  intentId?: string
): Promise<{ kind: PaymentErrorKind; transactionError: TransactionError }> => {
  const errorType = 'type' in error ? error.type : '';
  const isCardError = errorType === 'card_error';
  const isGenericDecline =
    isCardError &&
    'decline_code' in error &&
    error.decline_code === 'generic_decline';

  if (intentId && isGenericDecline) {
    const errorDetails = await paymentApi.getPaymentFailureReason(intentId);

    const isPrepaidCardError =
      errorDetails.success &&
      errorDetails.intent.errorCode === 'PREPAID_CARD_NOT_ACCEPTED';

    if (isPrepaidCardError) {
      return {
        kind: 'prepaidCardNotAllowed',
        transactionError: {
          code: 'PREPAID_CARD_NOT_ALLOWED',
        },
      };
    }
  }

  const errorKind = isCardError
    ? 'confirmIntentInvalid'
    : 'confirmIntentUnexpected';

  if (isCardError) {
    const declineCode =
      isCardError && 'decline_code' in error && error.decline_code
        ? error.decline_code
        : undefined;

    const errorCode = 'code' in error && error.code ? error.code : undefined;

    return {
      kind: errorKind,
      transactionError: { code: declineCode ?? errorCode ?? 'generic_decline' },
    };
  }

  const errorCode =
    'code' in error && error.code ? error.code : 'generic_decline';

  return {
    kind: errorKind,
    transactionError: {
      code: errorCode,
    },
  };
};

const fetchBillingInfo =
  async (): Promise<StripeBillingInfoDefaultValues | null> => {
    const response = await paymentApi.getBillingInfo();
    if (!response.success) {
      return null;
    }
    const stripeInfo: StripeBillingInfoDefaultValues = {
      ...response.billingInfo,
      address: response.billingInfo?.address
        ? {
            ...response.billingInfo.address,
            postal_code: response.billingInfo.address.postalCode,
          }
        : undefined,
    };

    return stripeInfo;
  };

export const paymentStoreStateCreator: StateCreator<PaymentStore> = (
  set,
  get
) => ({
  ...initialState,

  actions: {
    init: async (): Promise<void> => {
      const { stripe, stripeAccountCountry, status } = get();

      if (status === RemoteDataStatus.Loading) {
        return;
      }

      if (stripe) {
        // stripe instance is already loaded, so we can reuse the existing stripe instance and country
        set({
          ...initialState,
          stripe,
          stripeAccountCountry,
          billingInfo: await fetchBillingInfo(),
        });
        return;
      }

      let selectedStripeKey;
      if (stripeAccountCountry === 'US') {
        selectedStripeKey = stripeUSKey;
      } else if (stripeAccountCountry === 'EU') {
        selectedStripeKey = stripeEUKey;
      } else {
        // For backward compatibility, fallback to old key
        // TODO: Remove this after backend and frontend are released
        selectedStripeKey = stripeKey;
      }

      if (selectedStripeKey) {
        set({ status: RemoteDataStatus.Loading });
        const [stripe, billingInfo] = await Promise.all([
          loadStripe(selectedStripeKey, { locale: 'en' }).catch((err) => {
            console.error('failed to load stripe:', err);
            return null;
          }),
          await fetchBillingInfo(),
        ]);

        if (stripe) {
          set({
            stripe,
            status: RemoteDataStatus.Done,
            billingInfo,
            stripeAccountCountry,
          });
          return;
        }
      }

      set({ status: RemoteDataStatus.Error, errorKind: 'loadStripe' });
    },

    updateBillingInfo: async (data): Promise<void> => {
      try {
        const billingInfo: BillingInfo = {
          ...data,
          address: {
            ...data.address,
            postalCode: data.address?.postal_code,
          },
        };

        set({ status: RemoteDataStatus.Loading });

        const response = await paymentApi.updateBillingInfo(billingInfo);

        if (response.success) {
          set({ billingInfo: data });
        }
        set({ status: RemoteDataStatus.Done });
      } catch (e) {
        set({ status: RemoteDataStatus.Done });
        throw e;
      }
    },

    setIntent({ service }, data): void {
      set({
        payment: {
          service,
          setupIntent: data,
        },
      });
    },

    setupIntent: async ({ service }, data): Promise<IntentResult | void> => {
      if (get().status !== RemoteDataStatus.Done) {
        return;
      }

      set({ status: RemoteDataStatus.Loading });

      const response = await paymentApi.setupIntent(data);

      if (!response.success) {
        set({
          status: RemoteDataStatus.Error,
          errorKind: 'setupIntent',
        });

        return { success: false, transactionError: { code: 'internal_error' } };
      }

      set({
        status: RemoteDataStatus.Done,
        payment: {
          service,
        },
      });

      return {
        success: true,
        intentId: response.intentId,
        clientSecret: response.clientSecret,
      };
    },

    confirmIntent: async (
      elements,
      options
    ): Promise<OperationResult | void> => {
      const {
        returnUrl,
        intentId,
        paymentIntentClientSecret,
        setupIntentClientSecret,
      } = options;
      const { status, stripe } = get();

      // We want to indicate an error in this case, because it means the developer is using the store incorrectly
      if (!stripe) {
        return { success: false };
      }

      if (status === RemoteDataStatus.Loading) {
        return;
      }

      // This indicates that this confirming intent was not preceded by a setup intent
      if (!paymentIntentClientSecret && !setupIntentClientSecret) {
        console.error('Cannot confirm intent that was not setup.');
        return { success: false };
      }

      set({ status: RemoteDataStatus.Loading });

      const response = await paymentApi
        .confirmIntent({
          stripe,
          elements,
          paymentIntentClientSecret,
          setupIntentClientSecret,
          returnUrl,
        })
        .catch((error: Error): { success: false; error: Error } => ({
          success: false,
          error,
        }));

      if (!response.success) {
        const { kind, transactionError } = await fetchErrorCode(
          response.error,
          intentId
        );

        set({
          status: RemoteDataStatus.Error,
          errorKind: kind,
          transactionError,
        });

        return { success: false, transactionError };
      }

      set({
        status: RemoteDataStatus.Done,
        payment: null,
      });

      return { success: true };
    },

    reset: (): void => {
      const { stripe, stripeAccountCountry } = get();
      // Keep stripe instance and country if it was already loaded
      set({
        ...initialState,
        ...(stripe
          ? {
              stripe,
              stripeAccountCountry,
              status: RemoteDataStatus.Done,
            }
          : { stripeAccountCountry }),
      });
    },

    setStatus: (status, errorKind): void => {
      set({
        status,
        errorKind,
      });
    },

    setStripeAccountCountry: (country: string): void => {
      set({ stripeAccountCountry: country, stripe: null });
    },
  },
});
